import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-hot-toast';
import Iconify from '../iconify';
import PhoneVerificationModal from './PhoneVerificationModal';
import { validateEmail, sanitizeCodeInput, isCodeComplete, verifyInviteCode } from '../../service/inviteVerification';
import { signUpWithEmail } from '../../service/auth';
import { passwordRules, confirmPasswordRules } from '../../service/passwordValidation';
import { responsiveModalPaperSx, codeDigitFieldSx } from './modalLayout';

const CODE_LENGTH = 6;

const AccountSchema = Yup.object().shape({
  firstName: Yup.string().required('Required').min(2, 'Min 2 chars'),
  lastName: Yup.string().required('Required').min(2, 'Min 2 chars'),
  password: passwordRules,
  confirmPassword: confirmPasswordRules,
});

/**
 * KBT-174 — redeem an admin-issued invite (email + emailed confirmation
 * code) and hand off into the account-creation flow that already exists
 * (signUpWithEmail) rather than a parallel one, then finish with the same
 * PhoneVerificationModal every other new account goes through.
 *
 * Steps: 'redeem' (email + code) -> 'account' (name + password) ->
 * 'phone' (delegates entirely to the existing PhoneVerificationModal).
 */
export default function InviteRedemptionModal({
  open,
  handleClose,
  onAccountCreated,
  initialEmail = '',
}) {
  const [step, setStep] = React.useState('redeem');
  const [loading, setLoading] = React.useState(false);
  const [email, setEmail] = React.useState(initialEmail || '');
  const [emailError, setEmailError] = React.useState('');
  const [codeValues, setCodeValues] = React.useState(Array(CODE_LENGTH).fill(''));
  const [error, setError] = React.useState('');
  const [inviteId, setInviteId] = React.useState(null);
  const [quickBooksCustomerName, setQuickBooksCustomerName] = React.useState('');
  const [pendingUserData, setPendingUserData] = React.useState(null);

  const codeInputRefs = React.useRef([]);

  const resetAll = (emailPrefill = '') => {
    setStep('redeem');
    setEmail(emailPrefill);
    setEmailError('');
    setCodeValues(Array(CODE_LENGTH).fill(''));
    setError('');
    setInviteId(null);
    setQuickBooksCustomerName('');
    setPendingUserData(null);
  };

  React.useEffect(() => {
    if (open) resetAll(initialEmail || '');
  }, [open, initialEmail]);

  React.useEffect(() => {
    if (!open || step !== 'redeem' || !initialEmail) return undefined;
    const id = requestAnimationFrame(() => codeInputRefs.current[0]?.focus());
    return () => cancelAnimationFrame(id);
  }, [open, step, initialEmail]);

  const handleCodeChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...codeValues];
    next[index] = digit;
    setCodeValues(next);
    if (digit && index < CODE_LENGTH - 1) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !codeValues[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (event) => {
    event.preventDefault();
    const pasted = sanitizeCodeInput(event.clipboardData.getData('text'));
    if (!pasted) return;
    const next = Array(CODE_LENGTH).fill('');
    for (let i = 0; i < pasted.length; i += 1) next[i] = pasted[i];
    setCodeValues(next);
    const focusIndex = Math.min(pasted.length, CODE_LENGTH - 1);
    codeInputRefs.current[focusIndex]?.focus();
  };

  const handleVerify = async () => {
    const emailCheck = validateEmail(email);
    if (!emailCheck.valid) {
      setEmailError(emailCheck.error);
      return;
    }
    setEmailError('');

    const code = codeValues.join('');
    if (!isCodeComplete(code)) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    const result = await verifyInviteCode(emailCheck.formatted, code);
    if (result.success) {
      setInviteId(result.inviteId);
      setQuickBooksCustomerName(result.quickBooksCustomerName || '');
      setEmail(emailCheck.formatted);
      setStep('account');
    } else {
      setError(result.message || 'Invalid code');
      if (result.expired || result.rateLimited) {
        setCodeValues(Array(CODE_LENGTH).fill(''));
      }
    }
    setLoading(false);
  };

  const handleCreateAccount = async (values) => {
    setLoading(true);
    setError('');
    try {
      const displayName = `${values.firstName} ${values.lastName}`;
      const result = await signUpWithEmail(email, values.password, displayName, { inviteId });
      if (result && result.user) {
        setPendingUserData(result.user);
        setStep('phone');
      } else {
        // signUpWithEmail already toasts a specific error message
        setError('Could not create your account. Please try again.');
      }
    } catch (err) {
      console.error('Invite account creation error:', err);
      toast.error(err.message || 'Failed to create account');
    }
    setLoading(false);
  };

  const handlePhoneVerified = (verifiedUserData) => {
    onAccountCreated(verifiedUserData);
    handleClose();
    resetAll();
  };

  if (step === 'phone') {
    return (
      <PhoneVerificationModal
        open={open}
        handleClose={() => {
          handleClose();
          resetAll();
        }}
        onVerificationComplete={handlePhoneVerified}
        userData={pendingUserData}
      />
    );
  }

  return (
    <Modal
      open={open}
      onClose={() => {}}
      disableEscapeKeyDown
      aria-labelledby="invite-redemption-title"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Box
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-redemption-title"
        data-testid="invite-redemption-modal"
        sx={responsiveModalPaperSx}
      >
        {loading && (
          <LinearProgress
            sx={{ position: 'absolute', top: 0, left: 0, right: 0, '& .MuiLinearProgress-bar': { bgcolor: '#242258' } }}
          />
        )}

        {/* Header */}
        <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
          <IconButton
            onClick={() => {
              if (step === 'account') {
                setStep('redeem');
                setError('');
              } else {
                handleClose();
              }
            }}
            aria-label={step === 'account' ? 'Go back to code entry' : 'Close dialog'}
            data-testid="invite-modal-back"
            sx={{
              width: 36,
              height: 36,
              border: '1px solid #E5E7EB',
              borderRadius: '10px',
              '&:hover': { bgcolor: '#F9FAFB' },
            }}
          >
            <Iconify icon="eva:arrow-back-fill" width={16} sx={{ color: '#374151' }} />
          </IconButton>
        </Box>

        <Box sx={{ textAlign: 'center', pb: 2 }}>
          <Typography id="invite-redemption-title" sx={{ fontSize: '20px', fontWeight: 700, color: '#111827' }}>
            {step === 'redeem' ? 'Redeem Your Invite' : 'Set Up Your Account'}
          </Typography>
          <Typography sx={{ fontSize: '14px', color: '#6B7280', mt: 0.5 }}>
            {step === 'redeem'
              ? 'Enter the email and confirmation code from your invite'
              : quickBooksCustomerName
              ? `Welcome, ${quickBooksCustomerName}! Choose a password to finish setting up.`
              : 'Choose a password to finish setting up your account'}
          </Typography>
        </Box>

        <Box sx={{ px: 3, pb: 3 }}>
          {error && (
            <Box role="alert" aria-live="assertive" sx={{ py: 1, px: 1.5, mb: 2, bgcolor: '#FEF2F2', borderRadius: '12px', textAlign: 'center' }}>
              <Typography sx={{ fontSize: '13px', color: '#DC2626', fontWeight: 500 }}>{error}</Typography>
            </Box>
          )}

          {step === 'redeem' && (
            <>
              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#374151', mb: 0.5 }}>
                Email Address
              </Typography>
              <TextField
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={Boolean(emailError)}
                helperText={emailError}
                aria-label="Invited email address"
                autoComplete="email"
                data-testid="invite-email"
                fullWidth
                size="small"
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { height: 42, borderRadius: '12px', fontSize: '14px' } }}
              />

              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#374151', mb: 1, textAlign: 'center' }}>
                Confirmation Code
              </Typography>
              <Box
                sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 0.5, sm: 1 }, mb: 2 }}
                onPaste={handleCodePaste}
              >
                {Array.from({ length: CODE_LENGTH }).map((_, index) => (
                  <TextField
                    key={index}
                    inputRef={(el) => (codeInputRefs.current[index] = el)}
                    value={codeValues[index]}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(index, e)}
                    inputProps={{
                      maxLength: 1,
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      autoComplete: index === 0 ? 'one-time-code' : 'off',
                      'aria-label': `Code digit ${index + 1} of ${CODE_LENGTH}`,
                      'data-testid': `invite-code-${index}`,
                      style: { textAlign: 'center', fontSize: '20px', fontWeight: 600 },
                    }}
                    sx={codeDigitFieldSx}
                  />
                ))}
              </Box>

              <Button
                onClick={handleVerify}
                disabled={loading}
                fullWidth
                data-testid="invite-verify-btn"
                sx={{
                  height: 46,
                  bgcolor: '#242258',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 600,
                  borderRadius: '12px',
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: '#1a1a45' },
                  '&:disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' },
                }}
              >
                {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Verify'}
              </Button>
            </>
          )}

          {step === 'account' && (
            <Formik
              initialValues={{ firstName: '', lastName: '', password: '', confirmPassword: '' }}
              validationSchema={AccountSchema}
              onSubmit={handleCreateAccount}
            >
              {({ handleChange, handleSubmit, touched, errors, values }) => (
                <form onSubmit={handleSubmit} noValidate style={{ width: '100%', minWidth: 0, padding: 0, margin: 0, boxShadow: 'none' }}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, mb: 1.5 }}>
                    <TextField
                      name="firstName"
                      label="First Name"
                      value={values.firstName}
                      onChange={handleChange}
                      error={touched.firstName && Boolean(errors.firstName)}
                      helperText={touched.firstName && errors.firstName}
                      data-testid="invite-firstname"
                      fullWidth
                      size="small"
                    />
                    <TextField
                      name="lastName"
                      label="Last Name"
                      value={values.lastName}
                      onChange={handleChange}
                      error={touched.lastName && Boolean(errors.lastName)}
                      helperText={touched.lastName && errors.lastName}
                      data-testid="invite-lastname"
                      fullWidth
                      size="small"
                    />
                  </Box>
                  <TextField
                    name="password"
                    type="password"
                    label="Password"
                    value={values.password}
                    onChange={handleChange}
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                    data-testid="invite-password"
                    fullWidth
                    size="small"
                    sx={{ mb: 1.5 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Iconify icon="eva:lock-outline" width={16} sx={{ color: '#9CA3AF' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    name="confirmPassword"
                    type="password"
                    label="Confirm Password"
                    value={values.confirmPassword}
                    onChange={handleChange}
                    error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                    helperText={touched.confirmPassword && errors.confirmPassword}
                    data-testid="invite-confirm-password"
                    fullWidth
                    size="small"
                    sx={{ mb: 2 }}
                  />

                  <Box role="alert" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, py: 1.25, mb: 2, bgcolor: '#EFF6FF', borderRadius: '12px' }}>
                    <Iconify icon="eva:info-outline" width={16} sx={{ color: '#2563EB' }} />
                    <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#2563EB' }}>
                      Phone verification required next
                    </Typography>
                  </Box>

                  <Button
                    type="submit"
                    disabled={loading}
                    fullWidth
                    data-testid="invite-create-account-btn"
                    sx={{
                      height: 46,
                      bgcolor: '#242258',
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: 600,
                      borderRadius: '12px',
                      textTransform: 'none',
                      boxShadow: 'none',
                      '&:hover': { bgcolor: '#1a1a45' },
                      '&:disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' },
                    }}
                  >
                    {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Create Account'}
                  </Button>
                </form>
              )}
            </Formik>
          )}
        </Box>
      </Box>
    </Modal>
  );
}
